import { useMemo, useState, type ReactNode } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { BRAND, PARTNER_TYPE_LABEL } from '@dru/types';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type {
  AuditRow, CursorPage, PartnerDetail as PartnerDetailShape, PartnerLocation, PartnerStatus, PayeeRateCardRow,
} from '@dru/types';
import { PARTNER_STATUS_LABEL } from '@dru/types';
import {
  PhoneInput,
  AddressBlock, addressForm, addressLines, addressPayload, Alert, Button, ConfirmDialog, ContactBlock,
  DataTable, DetailHeader, DetailSection, ErrorState, Facts, IconAction, InfoTip, Input, RowActions, SearchInput, StatusBadge, Tabs,
  TextField, cn, type Column, type Tone, useIdempotencyKey, useToast, type AddressFormValue,
} from '@dru/ui';
import { ApiError } from '@dru/api-client';
import { Archive, Pencil } from 'lucide-react';
import { api } from '../../../lib/api';
import { AddressFields } from '../../../components/AddressFields';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { holds } from '../../../layouts/ConsoleShell';
import { EmergencyProviderSwitch } from '../components/EmergencyProviderSwitch';
import { Dialog } from '../../../components/Dialog';
import { LeadOrigin } from '../../ops/components/LeadOrigin';
import { VenueStaff } from '../../partner/routes/Staff';
import { VenueCatalogue } from '../../partner/components/VenueCatalogue';
import { PayeeRates, currentRateCard } from '../components/PayeeRates';
import { PayeeSettlements } from '../components/PayeeSettlements';
import { partnerServiceFacts } from '../components/PartnerServicesCard';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';

/**
 * One network partner — a hospital or a laboratory — in full.
 *
 * A page rather than the modal this used to be. A modal is right for one
 * decision taken and dismissed; this is a record somebody arrives at from a
 * search, reads across several sections, links a colleague to, and comes back
 * to.
 *
 * It opens on the one question the page exists to answer — can this partner
 * receive cases? — as a readiness list: contact, site, branches, tests,
 * capacity, staff and a rate card. For a partner still awaiting review that
 * list is the empanelment gate, and what is still missing is written beside
 * the approval, never only in a tooltip on a greyed-out button.
 *
 * Five tabs, grouped by what a reader is doing: Branches & tests, then Rates
 * & statements. The old tab names in a URL still land in the right place.
 *
 * Every action follows the permission that the API checks for it: Finance
 * reads this page for the commercial side and is offered nothing it would be
 * refused — no edit, no suspend, no staff or audit tabs it cannot read.
 */

interface TestRow { id: string; code: string; name: string; category: string }

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'network', label: 'Branches & tests' },
  { key: 'commercial', label: 'Rates & statements' },
  { key: 'staff', label: 'Users' },
  { key: 'activity', label: 'Activity' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** A section inside a grouped tab, and which tab it belongs to. */
const VIEWS = {
  branches: 'network', capabilities: 'network', rates: 'commercial', statements: 'commercial',
} as const;
type ViewKey = keyof typeof VIEWS;

/** `?tab=capabilities` written before the grouping still opens capabilities. */
const LEGACY: Record<string, { tab: TabKey; view?: ViewKey }> = {
  locations: { tab: 'network', view: 'branches' },
  capabilities: { tab: 'network', view: 'capabilities' },
  rates: { tab: 'commercial', view: 'rates' },
  statements: { tab: 'commercial', view: 'statements' },
};

export function PartnerDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const { permissions } = useAuth();
  // What this reader may do, from the same permissions the API checks.
  const can = {
    update: holds(permissions, 'partner.update'),
    empanel: holds(permissions, 'partner.empanel'),
    staff: holds(permissions, 'user.read'),
    audit: holds(permissions, 'audit.read'),
    money: holds(permissions, 'settlement.read'),
  };
  const tabs = TABS.filter((t) => (t.key === 'staff' ? can.staff
    : t.key === 'activity' ? can.audit
      : t.key === 'commercial' ? can.money : true));

  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') ?? 'overview';
  const legacy = LEGACY[rawTab];
  const wanted: TabKey = legacy?.tab ?? (TABS.some((t) => t.key === rawTab) ? rawTab as TabKey : 'overview');
  // A link to a tab this reader cannot open lands on the overview rather
  // than on a tab strip with nothing selected and a 403 underneath.
  const tab: TabKey = tabs.some((t) => t.key === wanted) ? wanted : 'overview';
  const rawView = params.get('view');
  const view: ViewKey | null = legacy?.view
    ?? (rawView && rawView in VIEWS && VIEWS[rawView as ViewKey] === tab ? rawView as ViewKey : null);

  const partner = useQuery<PartnerDetailShape>(() => api.get(`/partners/${id}`), [id]);
  // Counts for the figures and the readiness list. The branches tab fetches
  // its own list with the retired ones included; the staff tab does the
  // same. Each is asked only of a reader the API will answer.
  const branches = useQuery<{ items: PartnerLocation[] }>(() => api.get(`/partners/${id}/locations`), [id]);
  const staff = useQuery<{ items: Array<{ id: string; status: string; fullName: string; roles: Array<{ key: string }> }> } | null>(
    () => (can.staff ? api.get(`/partners/${id}/users`) : Promise.resolve(null)), [id, can.staff],
  );
  const rates = useQuery<{ items: PayeeRateCardRow[] } | null>(
    () => (can.money ? api.get(`/settlements/rates/PARTNER/${id}`) : Promise.resolve(null)), [id, can.money],
  );
  const [editing, setEditing] = useState(false);
  const [statusIntent, setStatusIntent] = useState<PartnerStatus | null>(null);

  const p = partner.data;
  useBreadcrumbTitle(p?.name);

  const open = (t: TabKey, v?: ViewKey) => {
    const next = new URLSearchParams(params);
    next.set('tab', t);
    if (v) next.set('view', v); else next.delete('view');
    setParams(next, { replace: true });
  };
  const setTab = (key: string) => open(key as TabKey);

  const branchCount = branches.data?.items.filter((b) => b.status === 'ACTIVE').length ?? null;
  const staffCount = staff.data ? staff.data.items.filter((u) => u.status === 'ACTIVE').length : null;
  const rateCard = rates.data ? currentRateCard(rates.data.items) : undefined;


  if (partner.error) {
    return (
      <div className="space-y-4">
        <ErrorState title="This partner could not be opened" detail={partner.error} onRetry={partner.reload} />
      </div>
    );
  }

  const typeLabel = p ? p.networkType?.name ?? (PARTNER_TYPE_LABEL[p.type] ?? p.type) : null;

  return (
    <div className="space-y-5">
      <DetailHeader
        title={p?.name ?? 'Loading…'}
        badges={p && (
          <StatusBadge tone={partnerTone(p.status)}>
            {PARTNER_STATUS_LABEL[p.status] ?? p.status}
          </StatusBadge>
        )}
        // Type, code, where and since when — the identity in one line, so
        // the cards below need not repeat any of it.
        subtitle={p ? (
          <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span>{typeLabel}</span>
            <span aria-hidden className="text-neutral-300">·</span>
            <span className="font-mono text-neutral-700">{p.code}</span>
            <span aria-hidden className="text-neutral-300">·</span>
            <span>{[p.locality, p.city, p.state].filter(Boolean).join(', ') || 'Address not recorded'}</span>
            <span aria-hidden className="text-neutral-300">·</span>
            <span>Partner since {formatDate(p.createdAt)}</span>
          </span>
        ) : 'Network partner'}
        actions={p && (
          <>
            {can.update && <Button variant="secondary" onClick={() => setEditing(true)}>Edit details</Button>}

            {/* The actions offered follow the lifecycle. Previously anything
                not ACTIVE offered "Empanel", which put an approve button on a
                declined application and on a terminated contract — both refused
                by the API, so the button existed only to fail. Approval for a
                partner awaiting review sits with its readiness list below. */}
            {can.empanel && p.status === 'ONBOARDING' && (
              <Button variant="secondary" onClick={() => setStatusIntent('REJECTED')}>
                Decline
              </Button>
            )}
            {can.empanel && p.status === 'ACTIVE' && (
              <Button variant="secondary" onClick={() => setStatusIntent('SUSPENDED')}>
                Suspend
              </Button>
            )}
            {can.empanel && p.status === 'SUSPENDED' && (
              <Button onClick={() => setStatusIntent('ACTIVE')}>Reinstate</Button>
            )}
          </>
        )}
      />
      <LeadOrigin partnerId={id} />

      {p && (
        <>
          {/* The counts in one quiet bar, each a way into its tab. Four large
              tiles for four small numbers took the first screen and repeated
              what the tabs already say. */}
          <nav aria-label="At a glance" className="flex flex-wrap divide-neutral-200 overflow-hidden rounded-card border border-neutral-200 bg-white shadow-card sm:divide-x max-sm:flex-col max-sm:divide-y">
            <GlanceItem
              value={branchCount ?? '—'} label={branchCount === 1 ? 'branch' : 'branches'}
              note={branches.error ? 'could not be read' : branches.data?.items.some((b) => b.isPrimary && b.code === 'MAIN') ? 'incl. main site' : 'plus main site'}
              onClick={() => open('network', 'branches')} />
            <GlanceItem
              value={p.capabilities.length} label={p.capabilities.length === 1 ? 'test it can run' : 'tests it can run'}
              note={p.capabilities.length ? undefined : 'none declared'} warn={!p.capabilities.length}
              onClick={() => open('network', 'capabilities')} />
            <GlanceItem value={p.dailyCapacity} label="employees a day" />
            {can.staff && (
              <GlanceItem
                value={staffCount ?? '—'} label={staffCount === 1 ? 'user' : 'users'}
                note={staff.error ? 'could not be read' : staffCount ? undefined : 'nobody yet'} warn={staffCount === 0}
                onClick={() => open('staff')} />
            )}
          </nav>

          {p.status === 'ONBOARDING' && (
            <SetupSteps
              partner={p}
              branchCount={branchCount}
              staffCount={staffCount}
              rateCard={rates.data ? rateCard ?? null : undefined}
              can={can}
              open={open}
              onEdit={() => setEditing(true)}
              onApprove={() => setStatusIntent('ACTIVE')}
            />
          )}
          {/* The "Can receive cases — N gaps to close" card for an empanelled
              partner was removed at the owner's request (31 Oct 2026); the
              readiness list still decides approval while ONBOARDING. */}
        </>
      )}

      <Tabs
        tabs={tabs.map((t) => ({ key: t.key, label: t.label }))}
        active={tab}
        onChange={setTab}
      />

      {p && tab === 'overview' && (
        <div className="space-y-4">
          {holds(permissions, 'partner.assignment-manage') && (
            <EmergencyProviderSwitch partnerId={p.id} enabled={!!p.emergencyProvider} onChanged={partner.reload} />
          )}
          <Overview partner={p} />
        </div>
      )}

      {p && tab === 'network' && (
        <>
          <SubNav
            label="Branches and tests"
            items={[
              { key: 'branches', label: 'Branches', hint: branchCount ?? undefined },
              { key: 'capabilities', label: 'Tests it can run', hint: p.capabilities.length },
            ]}
            active={view ?? 'branches'}
            onChange={(v) => open('network', v as ViewKey)}
          />
          {(view ?? 'branches') === 'branches' && (
            <Locations partner={p} canEdit={can.update} onChanged={() => { partner.reload(); branches.reload(); }} />
          )}
          {view === 'capabilities' && (
            <>
              {can.update && <Capabilities partner={p} onSaved={partner.reload} />}
              <VenueCatalogue partnerId={p.id} rows={p.capabilities} canEdit={can.update} canImport={can.update} onSaved={partner.reload} />
            </>
          )}
        </>
      )}

      {p && tab === 'commercial' && (
        <>
          <SubNav
            label="Rates and statements"
            items={[
              { key: 'rates', label: 'Rate cards' },
              { key: 'statements', label: 'Statements' },
            ]}
            active={view ?? 'rates'}
            onChange={(v) => open('commercial', v as ViewKey)}
          />
          {(view ?? 'rates') === 'rates' && (
            <PayeeRates payeeType="PARTNER" payeeId={p.id} catalogue={p.capabilities} onChanged={rates.reload} />
          )}
          {view === 'statements' && <PayeeSettlements payeeType="PARTNER" payeeId={p.id} />}
        </>
      )}

      {p && tab === 'staff' && (
        <VenueStaff venueId={p.id} platform />
      )}
      {p && tab === 'activity' && <Activity partnerId={p.id} />}

      {p && editing && (
        <EditDialog
          partner={p}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); partner.reload(); }}
        />
      )}
      {p && statusIntent && (
        <StatusDialog
          partner={p}
          next={statusIntent}
          onClose={() => setStatusIntent(null)}
          onDone={() => { setStatusIntent(null); partner.reload(); }}
          onTerminated={() => nav('/admin/partners')}
        />
      )}
    </div>
  );
}


function partnerTone(s: PartnerStatus): Tone {
  switch (s) {
    case 'ACTIVE': return 'covered';
    case 'ONBOARDING': return 'dueSoon';
    case 'SUSPENDED': return 'overdue';
    // Declined is a settled answer, not an alarm: neutral, like terminated.
    case 'REJECTED': return 'neutral';
    default: return 'neutral';
  }
}

/**
 * Setting a new partner up, one step at a time (owner's review, 14 Oct 2026).
 *
 * Registration asks only for the name, type, address and contact; everything
 * else is done here, in order, with the next step highlighted and one button
 * for it. Only "Tests it can run" stops approval — the API refuses an
 * empanelment without it — the rest can follow after the partner is live.
 */
function SetupSteps({ partner: p, branchCount, staffCount, rateCard, can, open, onEdit, onApprove }: {
  partner: PartnerDetailShape;
  branchCount: number | null;
  staffCount: number | null;
  rateCard: PayeeRateCardRow | null | undefined;
  can: { update: boolean; empanel: boolean; staff: boolean; money: boolean };
  open: (t: TabKey, v?: ViewKey) => void;
  onEdit: () => void;
  onApprove: () => void;
}) {
  const tests = p.capabilities.length;
  const steps: Array<{ key: string; title: string; done: boolean; detail: string; required?: boolean; action?: { label: string; run: () => void } }> = [
    {
      key: 'details', title: 'Details and contact',
      done: Boolean((p.contactPhone || p.contactEmail) && addressLines(p).length),
      detail: [p.contactName, p.contactEmail, [p.locality, p.city].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'Add who to call and where it is',
      action: can.update ? { label: 'Edit details', run: onEdit } : undefined,
    },
    {
      key: 'branch', title: 'Branch',
      done: (branchCount ?? 0) > 0,
      detail: (branchCount ?? 0) > 0
        ? `${branchCount} branch${branchCount === 1 ? '' : 'es'} — the first one is the registration address`
        : 'Employees go to the registration address until a branch is added',
      action: { label: 'Branches', run: () => open('network', 'branches') },
    },
    {
      key: 'tests', title: 'Tests it can run', required: true,
      done: tests > 0,
      detail: tests ? `${tests} test${tests === 1 ? '' : 's'}` : 'Needed before approval: what this partner can examine',
      action: can.update ? { label: 'Declare tests', run: () => open('network', 'capabilities') } : undefined,
    },
    {
      key: 'hours', title: 'Working hours and capacity',
      done: Boolean(p.operatingDays || p.operatingHours),
      detail: [p.operatingDays, p.operatingHours, `${p.dailyCapacity} employees a day`].filter(Boolean).join(' · '),
      action: can.update ? { label: 'Set hours and capacity', run: onEdit } : undefined,
    },
    ...(staffCount == null ? [] : [{
      key: 'staff', title: 'Users',
      done: staffCount > 0,
      detail: staffCount ? `${staffCount} active login${staffCount === 1 ? '' : 's'}` : 'Who at the partner receives employees and enters results',
      action: { label: 'Add user', run: () => open('staff') },
    }]),
    ...(rateCard === undefined ? [] : [{
      key: 'rates', title: 'Rates',
      done: Boolean(rateCard),
      detail: rateCard ? rateCard.name : `What ${BRAND.name} pays this partner`,
      action: { label: 'Rate cards', run: () => open('commercial', 'rates') },
    }]),
  ];
  const doneCount = steps.filter((x) => x.done).length;
  const current = steps.find((x) => !x.done)?.key ?? null;
  const ready = steps.filter((x) => x.required).every((x) => x.done);

  return (
    <section aria-labelledby="partner-setup" className="rounded-card border border-primary-200 bg-white p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 id="partner-setup" className="text-h4 text-neutral-900">Set up this partner</h2>
          <p className="text-small text-neutral-600">{doneCount} of {steps.length} done{ready ? ' · ready to approve' : ''}</p>
        </div>
        {can.empanel && (
          <Button onClick={onApprove} disabled={!ready}>Approve and empanel</Button>
        )}
      </div>
      <ol className="mt-4 space-y-2">
        {steps.map((x, i) => {
          const isCurrent = x.key === current;
          return (
            <li
              key={x.key}
              aria-current={isCurrent ? 'step' : undefined}
              className={cn(
                'flex flex-wrap items-center gap-3 rounded-input border px-3 py-2.5',
                isCurrent ? 'border-primary-300 bg-primary-50' : 'border-neutral-200',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-small font-semibold',
                  x.done ? 'bg-covered-100 text-covered-600' : isCurrent ? 'bg-primary-600 text-white' : 'bg-neutral-100 text-neutral-600',
                )}
              >
                {x.done ? '✓' : i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-body font-medium text-neutral-900">
                  {x.title}
                  {x.required && !x.done && <span className="ml-2 text-caption font-normal text-danger-600">needed to approve</span>}
                  {!x.required && !x.done && <span className="ml-2 text-caption font-normal text-neutral-500">can be done later</span>}
                </p>
                <p className="truncate text-small text-neutral-600">{x.detail}</p>
              </div>
              {x.action && (
                <Button size="sm" variant={isCurrent ? 'primary' : 'secondary'} onClick={x.action.run}>
                  {x.action.label}
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * The second level under a grouped tab: two sections, as pills.
 * Not a second tab strip — one strip per page is the rule — and in the URL
 * so a link to the rates opens on the rates.
 */
function SubNav({ label, items, active, onChange }: {
  label: string;
  items: Array<{ key: string; label: string; hint?: number }>;
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-1.5">
      {items.map((it) => {
        const on = it.key === active;
        return (
          <button
            key={it.key}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(it.key)}
            className={cn(
              'inline-flex h-8 max-md:h-10 items-center gap-1.5 rounded-btn border px-3 text-small font-medium transition-colors duration-hover',
              on ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
            )}
          >
            {it.label}
            {it.hint != null ? <span className="rounded-badge bg-neutral-100 px-1.5 text-caption tabular-nums text-neutral-600">{it.hint}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/* ── Overview ───────────────────────────────────────────────── */

function Overview({ partner: p }: { partner: PartnerDetailShape }) {
  // One "Edit details" in the header opens the one dialog these sections
  // share. Main column: what the venue does and when; side: how to reach it.
  // Code, type and registration date are in the header, not repeated here.
  const notRecorded = <span className="text-neutral-500">Not recorded</span>;
  return (
    <div className="grid items-start gap-4 xl:grid-cols-3">
      <div className="min-w-0 space-y-4 xl:col-span-2">
        <DetailSection
          title="Services and capacity"
          action={(
            <InfoTip label="About services and capacity">
              Hours and capacity are the partner's own; the rest is read from the tests it has declared.
              Change them with Edit details or under Branches &amp; tests.
            </InfoTip>
          )}
        >
          <Facts
            columns={3}
            facts={[
              { label: 'Open on', value: p.operatingDays ?? notRecorded },
              { label: 'Hours', value: p.operatingHours ?? notRecorded },
              // Capacity is in the bar above: across every company that books here.
              ...partnerServiceFacts(p.capabilities ?? []),
              p.website ? {
                label: 'Website', wide: true,
                value: <a href={p.website} target="_blank" rel="noreferrer" className="break-all text-primary-700 hover:underline">{p.website}</a>,
              } : null,
            ]}
          />
        </DetailSection>

        {/* The decision, where one has been recorded. Hidden for a partner
            still awaiting review — an empty "approved by: —" block reads as
            a lost record. The audit trail remains the record. */}
        {(p.approvedAt || p.rejectedAt) && (
          <DetailSection
            title="The decision"
            action={(
              <InfoTip label="About the decision">
                Shown here for convenience. The audit trail is the record, and it holds
                every change rather than only the latest one.
              </InfoTip>
            )}
          >
            <Facts
              columns={2}
              facts={[
                p.approvedAt ? { label: 'Approved by', value: p.approvedByName ?? 'No longer on the platform', hint: formatDateTime(p.approvedAt) } : null,
                p.rejectedAt ? { label: 'Declined by', value: p.rejectedByName ?? 'No longer on the platform', hint: formatDateTime(p.rejectedAt) } : null,
                p.rejectionReason ? { label: 'Why it was declined', value: p.rejectionReason, wide: true } : null,
              ]}
            />
          </DetailSection>
        )}
      </div>

      <div className="min-w-0 space-y-4">
        <DetailSection title="Who to call" className="[&_a]:break-all">
          <ContactBlock contact={p} />
        </DetailSection>
        <DetailSection
          title="Registered site"
          action={(
            <InfoTip label="About the registered site">
              Appointments with no branch chosen use it. Other sites an employee may
              be sent to are branches, under Branches &amp; tests.
            </InfoTip>
          )}
        >
          <AddressBlock address={p} />
        </DetailSection>
      </div>
    </div>
  );
}

/** One count in the at-a-glance bar; a button when it leads somewhere. */
function GlanceItem({ value, label, note, warn, onClick }: {
  value: ReactNode; label: string; note?: string; warn?: boolean; onClick?: () => void;
}) {
  const body = (
    <>
      <span className={cn('text-h4 font-semibold tabular-nums', warn ? 'text-overdue-600' : 'text-neutral-900')}>{value}</span>
      <span className="text-small text-neutral-700">{label}</span>
      {note && <span className="text-caption text-neutral-500">· {note}</span>}
    </>
  );
  // Basis only side by side: stacked on a phone, a basis sets the height.
  const cls = 'flex min-h-12 flex-wrap items-baseline gap-x-1.5 px-4 py-2.5 text-left sm:flex-1 sm:basis-40';
  return onClick ? (
    <button type="button" onClick={onClick}
            className={cn(cls, 'transition-colors duration-hover hover:bg-neutral-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500')}>
      {body}
    </button>
  ) : <div className={cls}>{body}</div>;
}

/* ── Capabilities ───────────────────────────────────────────── */

function Capabilities({ partner: p, onSaved }: {
  partner: PartnerDetailShape; onSaved: () => void;
}) {
  // Every test, followed to the end of the cursor: the list is grouped with an
  // all/none toggle per category, so a category showing three of its five
  // tests would silently record the venue as unable to run the other two.
  const catalogue = useQuery<TestRow[]>(
    () => fetchAll<TestRow>((cursor) => api.get<CursorPage<TestRow>>(
      `/tests?brief=true&limit=200${cursor ? `&cursor=${cursor}` : ''}`,
    )),
    [],
  );
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(p.capabilities.map((t) => t.id)),
  );
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const allTests = catalogue.data ?? [];
  const q = search.trim().toLowerCase();
  const visible = q
    ? allTests.filter((t) => `${t.name} ${t.code} ${t.category}`.toLowerCase().includes(q))
    : allTests;
  const byCategory = useMemo(() => visible.reduce<Record<string, TestRow[]>>((acc, t) => {
    (acc[t.category] ??= []).push(t);
    return acc;
  }, {}), [visible]);

  const dirty = selected.size !== p.capabilities.length
    || p.capabilities.some((t) => !selected.has(t.id));

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.post(`/partners/${p.id}/capabilities`, { testIds: [...selected] });
      setSaved(true);
      onSaved();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <DetailSection
      title={`Choose the tests it can run (${selected.size} selected)`}
      action={(
        <span className="flex items-center gap-2">
          <InfoTip label="About capabilities">
            Routing checks an employee's protocol against this list and refuses a venue
            that is missing a mandatory test unless somebody overrides it with a
            reason. Empanelment is refused entirely while this is empty.
          </InfoTip>
          <Button size="sm" variant="secondary" loading={busy} onClick={save} disabled={!dirty}>
            {dirty ? 'Save capabilities' : saved ? 'Saved' : 'No changes'}
          </Button>
        </span>
      )}
    >

      {catalogue.error && <Alert>{catalogue.error}</Alert>}
      {error && <Alert>{error}</Alert>}

      <div className="mt-3 flex items-center gap-3">
        <SearchInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search a test by name or code"
          aria-label="Search the test catalogue"
          className="flex-1"
        />
        {q && (
          <span className="shrink-0 text-caption text-neutral-500">
            {visible.length} of {allTests.length}
          </span>
        )}
      </div>

      <div className="mt-3 max-h-[28rem] space-y-3 overflow-y-auto rounded border border-neutral-100 p-3">
        {catalogue.loading && <p className="text-small text-neutral-500">Loading the catalogue…</p>}
        {q && visible.length === 0 && (
          <p className="text-small text-neutral-500">No test matches “{search.trim()}”.</p>
        )}
        {Object.entries(byCategory).map(([category, tests]) => (
          <div key={category}>
            <div className="flex items-center gap-2">
              <span className="text-caption font-medium uppercase tracking-wide text-neutral-500">
                {category.replace(/_/g, ' ').toLowerCase()}
              </span>
              <button
                type="button"
                aria-label={`${tests.every((t) => selected.has(t.id)) ? 'Clear' : 'Select'} every ${category.replace(/_/g, ' ').toLowerCase()} test`}
                onClick={() => setSelected((s) => {
                  const next = new Set(s);
                  const all = tests.every((t) => next.has(t.id));
                  for (const t of tests) { if (all) next.delete(t.id); else next.add(t.id); }
                  return next;
                })}
                className="inline-flex min-h-6 items-center tap-area rounded px-1.5 text-caption font-medium text-primary-600 hover:bg-primary-50 hover:text-primary-700"
              >
                {tests.every((t) => selected.has(t.id)) ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className="mt-1 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
              {tests.map((t) => (
                <label key={t.id} className="flex items-start gap-2 text-sm">
                  <Input
                    type="checkbox"
                    checked={selected.has(t.id)}
                    onChange={() => setSelected((s) => {
                      const next = new Set(s);
                      if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                      return next;
                    })}
                    className="mt-1"
                  />
                  <span className="min-w-0">
                    <span className="block truncate text-neutral-800">{t.name}</span>
                    <span className="block font-mono text-caption text-neutral-500">{t.code}</span>
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

/* ── Activity ───────────────────────────────────────────────── */

/**
 * The venue's own audit trail.
 *
 * Partner rows are written without a tenant, so this is readable by a platform
 * administrator and returns nothing for a company user — which is correct:
 * one customer has no business reading who else changed a shared venue.
 */
function Activity({ partnerId }: { partnerId: string }) {
  const trail = useQuery<CursorPage<AuditRow>>(
    () => api.get(`/audit?entityType=Partner&entityId=${partnerId}&limit=50`),
    [partnerId],
  );

  return (
    <DetailSection title="Activity">
      <DataTable<AuditRow>
        rows={trail.data?.items ?? []}
        loading={trail.loading}
        error={trail.error}
        onRetry={trail.reload}
        rowKey={(r) => r.id}
        empty={{ title: 'Nothing recorded yet', hint: 'Registration, empanelment and every change to this venue appear here.' }}
        columns={[
          {
            key: 'when', header: 'When',
            cell: (r) => (
              <span className="text-xs text-neutral-600">
                {formatDateTime(r.occurredAt)}
              </span>
            ),
          },
          {
            key: 'what', header: 'What',
            cell: (r) => <span className="text-sm text-neutral-900">{actionWords(r.action)}</span>,
          },
          {
            key: 'outcome', header: 'Outcome', mobileLabel: false,
            cell: (r) => (
              <StatusBadge tone={r.outcome === 'SUCCESS' ? 'covered' : 'overdue'}>
                {r.outcome === 'SUCCESS' ? 'Succeeded' : r.outcome === 'DENIED' ? 'Refused' : actionWords(r.outcome)}
              </StatusBadge>
            ),
          },
          {
            key: 'detail', header: 'Detail',
            cell: (r) => {
              const meta = (r.metadata ?? {}) as Record<string, unknown>;
              const reason = typeof meta.reason === 'string' ? meta.reason : null;
              return <span className="text-xs text-neutral-600">{reason ?? '—'}</span>;
            },
          },
        ]}
      />
    </DetailSection>
  );
}

/** `partner.status.update` → "Partner status update". */
function actionWords(key: string): string {
  const spaced = key.replace(/[._]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/* ── Dialogs ────────────────────────────────────────────────── */


function textFields(form: Record<string, string>, keys: readonly string[]) {
  const out: Record<string, string> = {};
  for (const k of keys) if (form[k]?.trim()) out[k] = form[k].trim();
  return out;
}

const CONTACT_KEYS = [
  'contactName', 'contactPhone', 'contactAltPhone', 'contactEmail',
  'emergencyPhone', 'website', 'operatingDays', 'operatingHours',
] as const;

function EditDialog({ partner, onClose, onSaved }: {
  partner: PartnerDetailShape; onClose: () => void; onSaved: () => void;
}) {
  const [address, setAddress] = useState<AddressFormValue>(addressForm(partner));
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<Record<string, string>>(() => ({
    code: partner.code,
    name: partner.name,
    dailyCapacity: String(partner.dailyCapacity),
    contactName: partner.contactName ?? '',
    contactPhone: partner.contactPhone ?? '',
    contactAltPhone: partner.contactAltPhone ?? '',
    contactEmail: partner.contactEmail ?? '',
    emergencyPhone: partner.emergencyPhone ?? '',
    website: partner.website ?? '',
    operatingDays: partner.operatingDays ?? '',
    operatingHours: partner.operatingHours ?? '',
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setBusy(true); setError(null); setFieldErrors({});
    try {
      await api.patch(`/partners/${partner.id}`, {
        name: form.name,
        ...(form.code.trim() && form.code.trim().toUpperCase() !== partner.code ? { code: form.code.trim().toUpperCase() } : {}),
        // A cleared box is "unchanged", not zero — the server refuses 0.
        ...(form.dailyCapacity.trim() ? { dailyCapacity: Number(form.dailyCapacity) } : {}),
        ...addressPayload(address),
        ...textFields(form, CONTACT_KEYS),
      });
      onSaved();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={`Edit ${partner.name}`} info="Appointments already booked keep the address recorded at the time — correcting this does not rewrite where a past examination happened." onClose={onClose} wide>

      <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
        <TextField label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        {/* Made from the name at registration; correct it here if needed. */}
        <TextField label="Partner code" value={form.code} mono error={fieldErrors.code}
          onChange={(v) => setForm({ ...form, code: v.toUpperCase().replace(/[^A-Z0-9-]/g, '') })} />
      </div>

      <div className="mt-4"><AddressFields value={address} onChange={setAddress} errors={fieldErrors} /></div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <TextField label="Contact" value={form.contactName} error={fieldErrors.contactName}
          onChange={(v) => setForm({ ...form, contactName: v })} />
        <PhoneInput label="Mobile number" value={form.contactPhone} error={fieldErrors.contactPhone}
          onChange={(v) => setForm({ ...form, contactPhone: v })} />
        <PhoneInput label="Alternate mobile" value={form.contactAltPhone} error={fieldErrors.contactAltPhone}
          onChange={(v) => setForm({ ...form, contactAltPhone: v })} />
        <PhoneInput label="Emergency phone" value={form.emergencyPhone} error={fieldErrors.emergencyPhone}
          onChange={(v) => setForm({ ...form, emergencyPhone: v })} />
        <TextField label="Email" value={form.contactEmail} error={fieldErrors.contactEmail}
          onChange={(v) => setForm({ ...form, contactEmail: v })} />
        <TextField label="Website" value={form.website} error={fieldErrors.website}
          onChange={(v) => setForm({ ...form, website: v })} />
        <TextField label="Open on" value={form.operatingDays}
          onChange={(v) => setForm({ ...form, operatingDays: v })} />
        <TextField label="Hours" value={form.operatingHours}
          onChange={(v) => setForm({ ...form, operatingHours: v })} />
      </div>

      <label className="mt-3 block">
        <span className="text-xs font-medium text-neutral-700">Employees per day</span>
        <Input
          type="number" min={1} max={2000}
          value={form.dailyCapacity}
          onChange={(e) => setForm({ ...form, dailyCapacity: e.target.value })}
          className="mt-1 w-40 text-right tabular-nums"
        />
      </label>

      {error && <Alert>{error}</Alert>}

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={form.name.trim().length < 2}>
          Save changes
        </Button>
      </div>
    </Dialog>
  );
}

function StatusDialog({ partner, next, onClose, onDone, onTerminated }: {
  partner: PartnerDetailShape;
  next: PartnerStatus;
  onClose: () => void;
  onDone: () => void;
  onTerminated: () => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(status: PartnerStatus) {
    setBusy(true); setError(null);
    try {
      await api.post(`/partners/${partner.id}/status`, { status, reason });
      if (status === 'TERMINATED') onTerminated(); else onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  // One dialog, four decisions. Each states what actually happens next,
  // because "are you sure" is answered yes by reflex and the four outcomes are
  // not equally reversible: a suspension is a pause, a decline is final.
  const COPY: Record<string, { title: string; what: string; confirm: string }> = {
    ACTIVE: partner.status === 'SUSPENDED'
      ? {
          title: `Reinstate ${partner.name}`,
          what: 'It becomes routable again and reappears in every picker.',
          confirm: 'Reinstate',
        }
      : {
          title: `Approve ${partner.name}`,
          what: 'Empanelling makes this venue routable: HR can send employees here from '
            + 'the moment it is saved.',
          confirm: 'Approve and empanel',
        },
    REJECTED: {
      title: `Decline ${partner.name}`,
      what: 'The application is closed and the venue never becomes routable. This cannot '
        + 'be undone — if the venue is viable later it is registered again, so the second '
        + 'review is its own record. Your reason is stored on the row and is where the '
        + 'next conversation with them starts.',
      confirm: 'Decline application',
    },
    SUSPENDED: {
      title: `Suspend ${partner.name}`,
      what: 'Suspending removes it from every picker until it is reinstated. Appointments already '
        + 'booked are not cancelled — reschedule them deliberately. If the relationship is over rather '
        + 'than paused, terminate instead: that is final and cannot be reinstated.',
      confirm: 'Suspend',
    },
    TERMINATED: {
      title: `Terminate ${partner.name}`,
      what: 'The relationship ends and the venue cannot be reinstated.',
      confirm: 'Terminate',
    },
  };
  const copy = COPY[next] ?? COPY.SUSPENDED;
  const declining = next === 'REJECTED';

  return (
    <Dialog title={copy.title} onClose={onClose}>
      <p className="mt-1 text-sm text-neutral-600">{copy.what}</p>

      <TextField
        label={declining ? 'Why is this application being declined?' : 'Reason'}
        value={reason}
        onChange={setReason}
        required
        autoFocus
        // The button below was disabled until this had five characters, and
        // nothing said so. A grey button with no explanation reads as a
        // broken button — which is exactly how this was reported. The rule is
        // stated before it is broken, and the shortfall is counted once
        // somebody starts typing.
        error={reason.length > 0 && reason.trim().length < MIN_REASON
          ? `${MIN_REASON - reason.trim().length} more character${MIN_REASON - reason.trim().length === 1 ? '' : 's'} needed.`
          : null}
        hint={`At least ${MIN_REASON} characters.`}
        info={declining
          ? 'Stored on the venue and recorded in the audit trail against your name.'
          : 'Recorded on the change, and read by whoever asks why later.'}
      />

      {/* A refusal that names the fix. Empanelment is refused outright while
          a venue has declared nothing it can perform, and the server says why
          — but "add its capabilities" is a thing you do on the tab above this
          dialog, and the message alone left people looking for a button. */}
      {error && (
        <Alert>
          {error}
          {/no tests|capabilit/i.test(error) && (
            <span className="mt-1.5 block">
              Close this, open <strong>Network › Capabilities</strong> on this page, and tick what
              {' '}{partner.name} can actually run. Empanelment is offered again once at least
              one is saved.
            </span>
          )}
        </Alert>
      )}

      <div className="mt-5 flex justify-between gap-2">
        {/* Terminating is offered alongside a suspension because that is the
            moment somebody realises the relationship is over, not just paused.
            It is never offered next to an approval or a decline. */}
        {next === 'SUSPENDED' && (
          <Button
            variant="secondary"
            className="border-danger-600/40 text-danger-600 hover:bg-danger-100"
            onClick={() => submit('TERMINATED')}
            loading={busy}
            disabled={reason.trim().length < MIN_REASON}
          >
            Terminate instead
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            variant={declining ? 'danger' : next === 'ACTIVE' ? 'success' : 'primary'}
            onClick={() => submit(next)}
            loading={busy}
            disabled={reason.trim().length < MIN_REASON}
          >
            {copy.confirm}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** What the server demands of a reason, said once so the form and the button agree. */
const MIN_REASON = 5;

/* ── Locations ──────────────────────────────────────────────── */

/**
 * The partner's branches. The registered site stays on the partner row;
 * these are the others a worker may be sent to, each with its own address,
 * hours and contact, and chosen at booking.
 */
function Locations({ partner: p, canEdit, onChanged }: {
  partner: PartnerDetailShape; canEdit: boolean; onChanged: () => void;
}) {
  const locations = useQuery<{ items: PartnerLocation[] }>(
    () => api.get(`/partners/${p.id}/locations?includeInactive=true`), [p.id]);
  const [dialog, setDialog] = useState<null | 'new' | PartnerLocation>(null);
  const [retiring, setRetiring] = useState<PartnerLocation | null>(null);
  const { toast } = useToast();

  const rows = locations.data?.items ?? [];
  const active = rows.filter((l) => l.status === 'ACTIVE').length;

  return (
    <div className="space-y-4">
      {/* The registered site is on Overview; it is not repeated here. */}
      <DetailSection
        title={`Branches (${active}${rows.length > active ? ` active, ${rows.length - active} retired` : ''})`}
        action={canEdit ? <Button size="sm" onClick={() => setDialog('new')}>Add a branch</Button> : undefined}
      >
        <DataTable<PartnerLocation>
          rows={rows} loading={locations.loading} error={locations.error} onRetry={locations.reload}
          rowKey={(l) => l.id}
          empty={{
            title: 'No other branches',
            hint: canEdit
              ? 'Employees go to the registered site. Add a branch when this partner runs more than one.'
              : 'Employees go to the registered site.',
            action: canEdit ? <Button size="sm" variant="secondary" onClick={() => setDialog('new')}>Add a branch</Button> : undefined,
          }}
          columns={([
            { key: 'name', header: 'Branch', alwaysVisible: true, cell: (l) => (
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-neutral-900">
                  {l.name}
                  {l.isPrimary && <StatusBadge tone="info">Primary</StatusBadge>}
                </div>
                <div className="font-mono text-xs text-neutral-500">{l.code}</div>
              </div>
            ) },
            { key: 'where', header: 'Address', cell: (l) => <span className="text-xs text-neutral-700">{l.formattedAddress ?? ([l.locality, l.city].filter(Boolean).join(', ') || '—')}</span> },
            { key: 'hours', header: 'Open', cell: (l) => <span className="text-xs text-neutral-700">{[l.operatingDays, l.operatingHours].filter(Boolean).join(' · ') || 'Not recorded'}</span> },
            { key: 'capacity', header: 'Employees a day', align: 'right', cell: (l) => (
              <span className="text-xs tabular-nums text-neutral-700">{l.dailyCapacity ?? <span className="text-neutral-500">Partner-wide</span>}</span>
            ) },
            { key: 'contact', header: 'Contact', hideOnMobile: true, cell: (l) => <span className="text-xs text-neutral-700">{[l.contactName, l.contactPhone].filter(Boolean).join(' · ') || '—'}</span> },
            { key: 'status', header: 'Status', mobileLabel: false, cell: (l) => (
              <StatusBadge tone={l.status === 'ACTIVE' ? 'covered' : 'neutral'}>{l.status === 'ACTIVE' ? 'Active' : 'Retired'}</StatusBadge>
            ) },
            canEdit ? { key: 'act', header: 'Actions', align: 'right', alwaysVisible: true, mobileLabel: '', cell: (l) => (
              <RowActions>
                <IconAction label={`Edit ${l.name}`} icon={Pencil} onClick={() => setDialog(l)} />
                {l.status === 'ACTIVE' && (
                  <IconAction label={`Retire ${l.name}`} icon={Archive} danger onClick={() => setRetiring(l)} />
                )}
              </RowActions>
            ) } : null,
          ] as Array<Column<PartnerLocation> | null>).filter((c): c is Column<PartnerLocation> => c !== null)}
        />
      </DetailSection>
      {dialog && (
        <LocationDialog
          partnerId={p.id}
          location={dialog === 'new' ? null : dialog}
          onClose={() => setDialog(null)}
          onDone={() => { setDialog(null); locations.reload(); onChanged(); }}
        />
      )}
      {/* Retiring was a bare word that acted on the first click. It takes the
          branch out of every booking picker, so it is confirmed like any other
          change that alters where workers can be sent. */}
      {retiring && (
        <ConfirmDialog
          title={`Retire ${retiring.name}?`}
          what="It stops being offered when an appointment is booked here."
          affects={<>
            Branch <span className="font-mono">{retiring.code}</span> of {p.name}. Appointments already booked
            there keep their address and are not moved — reschedule them deliberately. The branch stays on
            this list as retired and can be edited later.
          </>}
          confirmLabel="Retire branch"
          tone="danger"
          onConfirm={async () => {
            await api.del(`/partners/${p.id}/locations/${retiring.id}`);
            toast('success', `${retiring.name} retired.`);
            setRetiring(null);
            locations.reload();
            onChanged();
          }}
          onCancel={() => setRetiring(null)}
        />
      )}
    </div>
  );
}

function LocationDialog({ partnerId, location, onClose, onDone }: {
  partnerId: string; location: PartnerLocation | null; onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState<Record<string, string>>({
    code: location?.code ?? '', name: location?.name ?? '',
    contactName: location?.contactName ?? '', contactPhone: location?.contactPhone ?? '',
    contactEmail: location?.contactEmail ?? '', emergencyPhone: location?.emergencyPhone ?? '',
    operatingDays: location?.operatingDays ?? '', operatingHours: location?.operatingHours ?? '',
    dailyCapacity: location?.dailyCapacity != null ? String(location.dailyCapacity) : '',
    services: location?.services ?? '', notes: location?.notes ?? '',
  });
  const [isPrimary, setIsPrimary] = useState(location?.isPrimary ?? false);
  const [address, setAddress] = useState<AddressFormValue>(addressForm(location));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const idem = useIdempotencyKey();
  const set = (k: string) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true); setError(null); setFieldErrors({});
    const body = {
      code: form.code.toUpperCase(), name: form.name.trim(), isPrimary,
      ...addressPayload(address),
      ...textFields(form, ['contactName', 'contactPhone', 'contactEmail', 'emergencyPhone', 'operatingDays', 'operatingHours', 'services', 'notes']),
      ...(form.dailyCapacity ? { dailyCapacity: Number(form.dailyCapacity) } : {}),
    };
    try {
      if (location) await api.patch(`/partners/${partnerId}/locations/${location.id}`, body);
      else { await api.post(`/partners/${partnerId}/locations`, body, idem.key()); idem.rotate(); }
      onDone();
    } catch (e) {
      if (e instanceof ApiError) setFieldErrors(e.fieldErrors);
      setError(errorMessage(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog title={location ? `Edit ${location.name}` : 'Add a branch'} onClose={onClose} wide>
      <div className="grid gap-3 sm:grid-cols-2">
        <TextField label="Code" value={form.code} mono error={fieldErrors.code} onChange={(v) => set('code')(v.toUpperCase())} hint="Unique within this partner." />
        <TextField label="Name" value={form.name} error={fieldErrors.name} onChange={set('name')} placeholder="Vapi branch" />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-neutral-700">
        <Input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
        This is the primary site
      </label>
      <div className="mt-4">
        <h3 className="text-xs font-medium uppercase tracking-wide text-neutral-500">Where it is</h3>
        <div className="mt-2"><AddressFields value={address} onChange={setAddress} errors={fieldErrors} /></div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <TextField label="Contact" value={form.contactName} error={fieldErrors.contactName} onChange={set('contactName')} />
        <PhoneInput label="Phone" value={form.contactPhone} error={fieldErrors.contactPhone} onChange={set('contactPhone')} />
        <PhoneInput label="Emergency phone" value={form.emergencyPhone} error={fieldErrors.emergencyPhone} onChange={set('emergencyPhone')} />
        <TextField label="Email" value={form.contactEmail} error={fieldErrors.contactEmail} onChange={set('contactEmail')} />
        <TextField label="Open on" value={form.operatingDays} onChange={set('operatingDays')} placeholder="Mon–Sat" />
        <TextField label="Hours" value={form.operatingHours} onChange={set('operatingHours')} placeholder="8:00–20:00" />
        <TextField label="Employees per day" value={form.dailyCapacity} mono inputMode="numeric" error={fieldErrors.dailyCapacity}
          onChange={(v) => set('dailyCapacity')(v.replace(/\D/g, ''))} hint="Blank uses the partner-wide figure." />
        <TextField label="Services" value={form.services} onChange={set('services')} placeholder="OPD, X-ray, audiometry booth" />
        <TextField label="Notes" value={form.notes} onChange={set('notes')} className="sm:col-span-2" />
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={save} loading={busy} disabled={form.code.trim().length < 2 || form.name.trim().length < 2}>{location ? 'Save' : 'Add branch'}</Button>
      </div>
    </Dialog>
  );
}
